import React, { useEffect } from 'react';
import { getSellingPriceQuantityFactor } from 'services/products/calculations/getSellingPriceForMargin';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { Col, Row } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import apiProducts from 'api/products';
import Checkbox from 'components/layout/forms/Checkbox';
import getShownDifference from 'services/numbers/getShownDifference';
import getContributionMarginRatio from 'services/products/calculations/getContributionMarginRatio';
import getSellingPricePerUnit from 'services/products/calculations/getSellingPricePerUnit';
import getExpenseTotal from 'services/products/calculations/getExpenseTotal';
import getFormStateHandler from 'services/forms/getFormStateHandler';
import getSellingPriceTotal from 'services/products/calculations/getSellingPriceTotal';
import getSubproductTotalExpense from 'services/products/calculations/getSubproductTotalExpense';
import IProduct from 'api/interfaces/products/IProductGet';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import NumberWithDropdownInputField from 'components/layout/forms/NumberWithDropdownInputField';
import SearchDropdown from 'components/layout/forms/SearchDropdown';
import TextInputField from 'components/layout/forms/TextInputField';
import TrashIcon from 'components/layout/icons/TrashIcon';
import translations from 'services/translations/translations';

import {
  CALCULATE_PRICE_FROM_DETAILS,
  DETAIL_CUSTOM_PRODUCT_NAME,
  DETAIL_HAS_CUSTOM_PRODUCT_NAME,
  EXPENSE,
  EXPENSE_UNIT,
  PRODUCT_ID,
  QUANTITY,
  QUANTITY_UNIT,
  SELLING_PRICE,
  SELLING_PRICE_UNIT,
  WASTE_PERCENTAGE,
} from 'services/products/productConfigurationAttributeTypes';

import IValueWithLabel from 'interfaces/forms/IValueWithLabel';
import {
  MAX_QUANTITY_DIGITS,
  MAX_WASTE_PERCENTAGE_DIGITS,
} from 'services/offers/inputLimits';
import { withTargetMargin } from '../targetMargin';
import getMarginRatioClassName from '../getMarginRatioClassName';
import useMarginInputs from '../useMarginInputs';
import getSellingPriceUnitOptions from '../../../../services/getSellingPriceUnitOptions';
import getUnitOptions from '../../../../services/getUnitOptions';
import IDetail from '../../IDetail';

const SubproductDetail = ({
  detail,
  locked,
  removeDetail,
  setDetail,
  subproductQuantity,
  targetMarginPercent,
}: {
  detail: IDetail;
  locked: boolean;
  removeDetail: () => void;
  setDetail: (updatedField: string) => (detail: IDetail) => void;
  subproductQuantity: number;
  // The target margin % of the window for the rows following it
  targetMarginPercent: string;
}): JSX.Element | null => {
  // A new detail prices itself for its target margin until its price is set. When that
  // changes its price, the material row recalculates both its expense and its price.
  const setPricedDetail = (updatedField: string) => (updated: IDetail) => {
    const priced = withTargetMargin(
      detail,
      updated,
      getSubproductTotalExpense,
      targetMarginPercent
    );
    setDetail(
      priced.sellingPrice === updated.sellingPrice
        ? updatedField
        : CALCULATE_PRICE_FROM_DETAILS
    )(priced);
  };

  const { t } = useTranslation([translations.common, translations.offers]);
  const units = useSelector((state: any) => state.products.units);

  useEffect(() => {
    const { expenseUnit } = detail;

    const sellingPriceUnit =
      detail.sellingPriceUnit === '' ||
      detail.sellingPriceUnit === detail.quantityUnit
        ? detail.sellingPriceUnit
        : detail.quantityUnit;

    const changed =
      detail.expenseUnit !== expenseUnit ||
      detail.sellingPriceUnit !== sellingPriceUnit;

    if (!changed) {
      return;
    }

    setPricedDetail('')({
      ...detail,
      expenseUnit,
      sellingPriceUnit,
    });
  }, [detail, setDetail]);

  const loadProducts = async (inputValue: string) => {
    const list = (await apiProducts.get(
      100,
      0,
      inputValue,
      undefined,
      false
    )) as IProduct[];
    return list.map((product: IProduct) => ({
      label: [null, ''].includes(product.displayName)
        ? product.name
        : product.displayName,
      value: product.id,
    }));
  };

  const offerIsLocked = () => locked;

  const sellingPriceUnitOptions = getSellingPriceUnitOptions(
    detail.quantityUnit,
    detail.key.toString(),
    t
  );

  const handleFormState = (fieldType: string) => {
    return getFormStateHandler(detail, setPricedDetail(fieldType));
  };

  const handleProductChange = (product: IValueWithLabel | null) => {
    setPricedDetail(PRODUCT_ID)({
      ...detail,
      product,
    });
  };

  const productSelector = detail.hasCustomProductName ? (
    <TextInputField
      value={detail.customProductName || ''}
      disabled={offerIsLocked()}
      onChange={handleFormState(DETAIL_CUSTOM_PRODUCT_NAME)}
      name="customProductName"
    />
  ) : (
    <SearchDropdown
      className="text-field search-field"
      disabled={offerIsLocked()}
      loadOptions={loadProducts}
      name="product"
      onChange={handleProductChange}
      placeholder={t(`${translations.common}:Search`)}
      required
      value={detail.product}
    />
  );

  const toggleHasCustomProductName = () => {
    const newValue = !detail.hasCustomProductName;
    setPricedDetail(DETAIL_HAS_CUSTOM_PRODUCT_NAME)({
      ...detail,
      hasCustomProductName: newValue,
    });
  };

  const expense = getSubproductTotalExpense(detail);
  const expenseTotal = getExpenseTotal([], [detail], subproductQuantity);
  const sellingPriceTotal = getSellingPriceTotal(
    [],
    [detail],
    subproductQuantity
  );
  // The margins shown: the shown selling price minus the shown expense, so they add up
  const contributionMargin = getShownDifference(
    getSellingPricePerUnit([], [detail]),
    expense
  );
  const contributionMarginTotal = getShownDifference(
    sellingPriceTotal,
    expenseTotal
  );
  const contributionMarginRatio = getContributionMarginRatio([], [detail]);

  // A typed margin sets the selling price; a price calculated from the details follows it
  const marginRatioClassName = getMarginRatioClassName(
    contributionMarginRatio,
    detail.sellingPrice
  );

  const marginInputs = useMarginInputs({
    contributionMargin,
    contributionMarginRatio,
    expense,
    quantityFactor: getSellingPriceQuantityFactor(
      detail.sellingPriceUnit,
      numericStringToFloat(detail.quantity)
    ),
    setSellingPrice: (sellingPrice: string) =>
      setPricedDetail(SELLING_PRICE)({ ...detail, sellingPrice }),
  });

  const SMALL_COLUMN_CLASS = 'small-column';

  return (
    <Row>
      <Col xs="1" className="mt-n2">
        <Checkbox
          checked={detail.hasCustomProductName}
          label=""
          onChange={toggleHasCustomProductName}
        />
      </Col>
      <Col>{productSelector}</Col>
      <Col>
        <NumberWithDropdownInputField
          disabled={offerIsLocked()}
          dropdownName="quantityUnit"
          dropdownOnChange={handleFormState(QUANTITY_UNIT)}
          dropdownOptions={getUnitOptions(units, t)}
          dropdownValue={detail.quantityUnit}
          maxIntegerDigits={MAX_QUANTITY_DIGITS}
          numberName="quantity"
          numberOnChange={handleFormState(QUANTITY)}
          numberValue={detail.quantity}
          required
        />
      </Col>
      <Col>
        <NumberWithDropdownInputField
          disabled={offerIsLocked()}
          dropdownDisabled
          dropdownName="expenseUnit"
          dropdownOnChange={handleFormState(EXPENSE_UNIT)}
          dropdownOptions={sellingPriceUnitOptions}
          dropdownValue={detail.expenseUnit}
          numberName="expense"
          numberOnChange={handleFormState(EXPENSE)}
          numberValue={detail.expense}
          required
        />
      </Col>
      <Col xs="1" className={SMALL_COLUMN_CLASS}>
        <NumberWithUnitInputField
          disabled={offerIsLocked()}
          maxIntegerDigits={MAX_WASTE_PERCENTAGE_DIGITS}
          name="wastePercentage"
          onChange={handleFormState(WASTE_PERCENTAGE)}
          required
          unit="%"
          value={detail.wastePercentage}
        />
      </Col>

      <Col xs="1" className={SMALL_COLUMN_CLASS}>
        <NumberWithUnitInputField
          disabled
          unit="€"
          value={formatGroupedNumber(expense)}
        />
      </Col>

      <Col>
        <NumberWithDropdownInputField
          disabled={offerIsLocked()}
          dropdownDisabled
          dropdownName="sellingPriceUnit"
          dropdownOnChange={handleFormState(SELLING_PRICE_UNIT)}
          dropdownOptions={sellingPriceUnitOptions}
          dropdownValue={detail.sellingPriceUnit}
          numberName="sellingPrice"
          numberOnChange={handleFormState(SELLING_PRICE)}
          numberValue={detail.sellingPrice}
          required
        />
      </Col>

      <Col xs="1" className={SMALL_COLUMN_CLASS}>
        <NumberWithUnitInputField
          disabled={offerIsLocked()}
          onBlur={marginInputs.eur.onBlur}
          onChange={marginInputs.eur.onChange}
          unit="€"
          value={marginInputs.eur.value}
        />
      </Col>

      <Col xs="1" className={SMALL_COLUMN_CLASS}>
        <NumberWithUnitInputField
          disabled
          unit="€"
          value={formatGroupedNumber(expenseTotal)}
        />
      </Col>

      <Col xs="1" className={SMALL_COLUMN_CLASS}>
        <NumberWithUnitInputField
          disabled
          unit="€"
          value={formatGroupedNumber(sellingPriceTotal)}
        />
      </Col>

      <Col xs="1" className={SMALL_COLUMN_CLASS}>
        <NumberWithUnitInputField
          disabled
          unit="€"
          value={formatGroupedNumber(contributionMarginTotal)}
        />
      </Col>

      <Col className={`${SMALL_COLUMN_CLASS} ${marginRatioClassName}`}>
        <NumberWithUnitInputField
          disabled={offerIsLocked()}
          onBlur={marginInputs.percent.onBlur}
          onChange={marginInputs.percent.onChange}
          unit="%"
          value={marginInputs.percent.value}
        />
      </Col>

      <Col className="icon-column pl-2">
        <TrashIcon onClick={removeDetail} />
      </Col>
    </Row>
  );
};

export default SubproductDetail;
